// Persists which request / agreement / signatory a UAE Pass signing session was
// started for. The provider redirects the browser back to a fixed return URL
// that carries no such context, so we stash it here before redirecting and read
// it on return. sessionStorage survives the cross-origin round-trip within the
// same tab and is scoped per-tab, so parallel tabs don't clash.
const SIGN_CONTEXT_KEY = 'listingRequest.uaePassSignContext'

export interface AgreementSignContext {
  requestId: string
  agreementId: string
  signatoryId: string
  /**
   * Where signing began when it was not the request — a funding window extension
   * is accepted from its property or its Marketplace page, so the seller lands
   * back there. An in-app path only (see `isInAppPath`).
   */
  returnPath?: string
}

// Read back from storage, so it is checked before it is followed: one leading
// slash, never two — `//host` is another site.
export function isInAppPath(path: unknown): path is string {
  return typeof path === 'string' && path.startsWith('/') && !path.startsWith('//')
}

export function saveSignContext(context: AgreementSignContext): void {
  sessionStorage.setItem(SIGN_CONTEXT_KEY, JSON.stringify(context))
}

export function readSignContext(): AgreementSignContext | null {
  const raw = sessionStorage.getItem(SIGN_CONTEXT_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<AgreementSignContext>
    if (!parsed.requestId || !parsed.agreementId || !parsed.signatoryId) return null
    return {
      requestId: parsed.requestId,
      agreementId: parsed.agreementId,
      signatoryId: parsed.signatoryId,
      ...(isInAppPath(parsed.returnPath) && { returnPath: parsed.returnPath }),
    }
  } catch {
    return null
  }
}

export function clearSignContext(): void {
  sessionStorage.removeItem(SIGN_CONTEXT_KEY)
}
